import { cn } from '@/lib/cn';

/**
 * 阅读进度条（工具栏正下方）。
 *
 * 两条与「数据从哪来」有关的决定：
 * 1. **以本地位置为准，服务端 `progressPct` 只在还没滚动时兜底**。
 *    服务端的百分比是每 30 秒才落一次库的，滚动过程中用它会出现"读了半页进度条不动"，
 *    反而让人以为进度没保存。页面卸载前仍会照常上报，所以本地值最终会同步上去。
 * 2. **句子序号用 1 起显示**（`第 3/128 句`），和用户数页数的直觉一致；
 *    `globalIndex` 本身是 0 起的，换算放在这里，避免每个调用点各减一次。
 */
export function ProgressBar({
  pct,
  currentIndex,
  totalSentences,
  finished = false,
  className,
}: {
  /** 0–100；超出范围会被夹住，避免脏数据显示成负宽度 */
  pct: number;
  /** 当前可见句的 globalIndex（0 起） */
  currentIndex: number;
  totalSentences: number;
  finished?: boolean;
  className?: string;
}) {
  const clamped = Math.min(100, Math.max(0, pct));
  const position = totalSentences > 0 ? Math.min(currentIndex + 1, totalSentences) : 0;

  return (
    <div className={cn('space-y-1', className)}>
      <div
        role="progressbar"
        aria-label="阅读进度"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamped)}
        aria-valuetext={totalSentences > 0 ? `第 ${position} 句，共 ${totalSentences} 句` : '正文为空'}
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
          style={{ width: `${clamped}%` }}
        />
      </div>

      <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>{totalSentences > 0 ? `第 ${position}/${totalSentences} 句` : '正文为空'}</span>
        <span className="flex items-center gap-2">
          {finished && <span className="text-[var(--success)]">已读完</span>}
          <span>{Math.round(clamped)}%</span>
        </span>
      </div>
    </div>
  );
}
